import React from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, Shield, Heart, FileText, Home as HomeIcon, Layers } from 'lucide-react';
import { MF_DISCLAIMER, NonMFNote } from '@/components/ComplianceNotice';

const Services = () => {
  const navigate = useNavigate();

  const services = [
    {
      icon: <TrendingUp className="w-12 h-12" />,
      title: 'Mutual Funds',
      description: 'Mutual fund distribution services – scheme information and comparison, SIP and lump-sum investment facilitation, and periodic portfolio reviews based on the investor\'s objectives and risk profile.',
      features: [
        'SIP investment facilitation',
        'Lump-sum investment facilitation',
        'Mutual fund scheme information and comparison',
        'Asset allocation information',
        'Retirement-goal investing assistance'
      ],
      isMF: true,
      link: '/services/mutual-funds'
    },
    {
      icon: <Shield className="w-12 h-12" />,
      title: 'Fixed Deposits',
      description: 'Guidance on suitable fixed-deposit options based on safety, tenure, interest requirements and liquidity needs.',
      features: [
        'Bank fixed deposits',
        'Corporate fixed deposits',
        'Tenure selection guidance',
        'Interest payout options',
        'Liquidity considerations'
      ],
      link: '/services/fixed-deposits'
    },
    {
      icon: <Heart className="w-12 h-12" />,
      title: 'Insurance',
      description: 'Support in understanding protection requirements and identifying suitable insurance solutions for financial security.',
      features: [
        'Life insurance',
        'Health insurance',
        'Family financial protection',
        'Need-based cover assessment',
        'Policy review guidance'
      ],
      link: '/services/insurance'
    },
    {
      icon: <FileText className="w-12 h-12" />,
      title: 'Bonds',
      description: 'Access to suitable fixed-income opportunities for investors seeking income, diversification and capital stability.',
      features: [
        'Government bonds',
        'Corporate bonds',
        'Coupon income guidance',
        'Credit risk awareness',
        'Portfolio diversification'
      ],
      link: '/services/bonds'
    },
    {
      icon: <Layers className="w-12 h-12" />,
      title: 'Alternative Investment Funds (AIFs)',
      description: 'Access specialised investment strategies such as private equity, private credit, real estate, infrastructure and long-short strategies for eligible investors seeking opportunities beyond traditional investments.',
      features: [
        'Private equity strategies',
        'Private credit opportunities',
        'Infrastructure investments',
        'Long-short strategies',
        'Suitability assessment'
      ],
      link: '/appointment?service=AIF+Consultation',
      cta: 'Know More',
      isAIF: true
    },
    {
      icon: <HomeIcon className="w-12 h-12" />,
      title: 'Real Estate',
      description: 'Guidance and assistance relating to selected real-estate opportunities based on client requirements.',
      features: [
        'Property evaluation',
        'Location assessment',
        'Long-term suitability',
        'Liquidity considerations',
        'Documentation support'
      ],
      link: '/services/real-estate'
    }
  ];

  return (
    <div className="min-h-screen" data-testid="services-page">
      {/* Hero */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-[#FDF8F5]">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-medium text-[#A0451E] mb-6" data-testid="services-heading">
            Our Services
          </h1>
          <p className="text-base font-body text-[#4A4A4A] max-w-3xl mx-auto leading-relaxed">
            Mutual fund distribution and investor assistance as an AMFI-registered Mutual Fund Distributor (ARN: 116347), along with other financial products arranged through their respective providers.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-[#FDF8F5] rounded-2xl p-8 border border-[#E5E5E5] card-hover cursor-pointer"
                onClick={() => navigate(service.link)}
                data-testid={`service-detail-card-${index}`}
              >
                <div className="text-[#D97706] mb-4">{service.icon}</div>
                <span
                  className={`inline-block text-xs font-body font-semibold px-3 py-1 rounded-full mb-3 ${service.isMF ? 'bg-[#A0451E] text-white' : 'bg-white border border-[#E5E5E5] text-[#737373]'}`}
                  data-testid={`service-label-${index}`}
                >
                  {service.isMF ? 'AMFI-registered distribution (ARN: 116347)' : 'Not covered by AMFI registration'}
                </span>
                <h2 className="text-2xl font-heading font-medium text-[#A0451E] mb-4">
                  {service.title}
                </h2>
                <p className="text-sm font-body text-[#4A4A4A] leading-relaxed mb-6">
                  {service.description}
                </p>
                <div className="space-y-2 mb-6">
                  {service.features.map((feature, fIndex) => (
                    <div key={fIndex} className="flex items-center text-sm font-body text-[#4A4A4A]">
                      <div className="w-1.5 h-1.5 bg-[#D97706] rounded-full mr-3"></div>
                      {feature}
                    </div>
                  ))}
                </div>
                <button className="text-[#D97706] font-semibold text-sm hover:underline font-body" data-testid={service.isAIF ? 'aif-services-know-more-btn' : undefined}>
                  {service.cta || 'Learn More'} →
                </button>
              </div>
            ))}
          </div>

          <p className="text-xs font-body text-[#737373] leading-relaxed mt-8 max-w-4xl italic" data-testid="aif-disclaimer">
            <strong className="text-[#A0451E] not-italic">Note on AIFs:</strong> AIFs are complex, high-risk and generally illiquid investment products intended for eligible investors. Investment is subject to applicable regulations, minimum investment requirements and suitability assessment.
          </p>
          <NonMFNote testId="non-mf-note-services" className="mt-3 max-w-4xl" />
          <p className="text-xs font-body font-semibold text-[#A0451E] mt-3">{MF_DISCLAIMER}</p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 section-bg-secondary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-heading font-medium text-[#A0451E] mb-6">
            Not Sure Which Service is Right for You?
          </h2>
          <p className="text-base font-body text-[#4A4A4A] mb-8 leading-relaxed">
            Schedule a consultation with our team to discuss your financial goals and find the right solution.
          </p>
          <button
            onClick={() => navigate('/appointment')}
            className="btn-primary"
            data-testid="services-book-appointment-btn"
          >
            Book an Appointment
          </button>
        </div>
      </section>
    </div>
  );
};

export default Services;